import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button, TextField } from '@/components/ui';
import { DevScenarioMenu } from '@/components/dev/DevScenarioMenu';
import { useAddPatient } from '../hooks/useAddPatient';
import { addPatientSchema, type AddPatientFormValues } from '../schemas/addPatient.schema';
import type { AddPatientResult } from '../types/patient.types';

interface AddPatientFormProps {
  onSuccess?: (result: AddPatientResult) => void;
}

export function AddPatientForm({ onSuccess }: AddPatientFormProps) {
  const { t } = useTranslation();
  const addPatient = useAddPatient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddPatientFormValues>({
    resolver: zodResolver(addPatientSchema),
    defaultValues: { cnp: '', activationCode: '' },
  });

  const result = addPatient.data;
  // A non-success outcome (InvalidCode, AlreadyAttached, …) maps to a localized message.
  const outcomeError =
    result && !result.success ? t(`patients.add.outcome.${result.outcome}`) : null;
  const requestError = addPatient.isError ? t('errors.network') : null;

  const onSubmit = handleSubmit((values) => {
    addPatient.mutate(values, {
      onSuccess: (res) => {
        if (res.success) {
          reset();
          onSuccess?.(res);
        }
      },
    });
  });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      {result?.success && (
        <div role="status" className="rounded-input bg-success-light px-3.5 py-2.5 text-sm text-success">
          {t(`patients.add.outcome.${result.outcome}`)}
          {result.attachedAt && result.attachedAt.length > 0 && (
            <span className="mt-1 block">
              {t('patients.add.attached_at', { emails: result.attachedAt.join(', ') })}
            </span>
          )}
        </div>
      )}
      {(outcomeError || requestError) && (
        <div role="alert" className="rounded-input bg-danger-light px-3.5 py-2.5 text-sm text-danger">
          {outcomeError ?? requestError}
        </div>
      )}

      <TextField
        label={t('patients.add.cnp')}
        inputMode="numeric"
        autoComplete="off"
        maxLength={13}
        placeholder={t('patients.add.cnp_placeholder')}
        error={errors.cnp ? t(errors.cnp.message ?? '') : undefined}
        {...register('cnp')}
      />

      <TextField
        label={t('patients.add.code')}
        autoComplete="off"
        placeholder={t('patients.add.code_placeholder')}
        error={errors.activationCode ? t(errors.activationCode.message ?? '') : undefined}
        {...register('activationCode')}
      />

      <p className="text-xs text-ink-mute">{t('patients.add.hint')}</p>

      <Button type="submit" variant="inverse" size="lg" block loading={addPatient.isPending}>
        {t('patients.add.submit')}
      </Button>

      {/* Dev-only: fires this form's mutation against a mocked wire response,
          so the banners above render the scenario through the real code path. */}
      <DevScenarioMenu run={(input) => addPatient.mutate(input)} />
    </form>
  );
}
